import { useCurrentUser } from '@/hooks/useCurrentUser'
import { assetPath } from '@/utils/assetPath'
import { useUserSummary } from '@/hooks/api/useUsers'
import { useNavigate, Link } from '@tanstack/react-router'
import { UserMenu } from './UserMenu'
import { DesktopSearchBar } from './DesktopSearchBar'
import { Plus } from 'lucide-react'
import HomeIcon from '@/icons/home'
import { CreateOptionsDropdown } from './CreateOptionsDropdown'
import { CreateCollectionFlow } from '@/components/create/collection/CreateCollectionFlow'
import { CreateProductFlow } from '@/components/create/product/CreateProductFlow'
import PopupSettings from '@/components/settings/desktop/PopupSettings'
import { useRef, useState, useEffect } from 'react'
import { useProtectedAction } from '@/hooks/useProtectedAction'

export const DesktopNavbar = () => {
    const navigate = useNavigate()
    const { user } = useCurrentUser()
    const userId = user?.uid
    const { withAuth } = useProtectedAction()

    const { data: userSummary } = useUserSummary(userId)
    const profileImage = userSummary?.avatarUrl || 'empty-image'
    const navRef = useRef<HTMLElement>(null)

    // Update CSS root variable --header-height based on nav height
    useEffect(() => {
        const navEl = navRef.current
        if (!navEl) return

        const updateHeaderHeight = () => {
            const height = navEl.getBoundingClientRect().height
            const computed = window.getComputedStyle(navEl)
            const marginTop = parseFloat(computed.marginTop) || 0
            const marginBottom = parseFloat(computed.marginBottom) || 0
            const totalHeight = Math.round(height + marginTop + marginBottom)
            document.documentElement.style.setProperty(
                '--header-height',
                `${totalHeight}px`
            )
        }

        updateHeaderHeight()

        const resizeObserver = new ResizeObserver(() => updateHeaderHeight())
        resizeObserver.observe(navEl)

        return () => {
            resizeObserver.disconnect()
            document.documentElement.style.removeProperty('--header-height')
        }
    }, [])

    // Dropdown
    const [createOptionsOpen, setCreateOptionsOpen] = useState(false)
    const createButtonRef = useRef<HTMLButtonElement>(null)
    const [newProductOpen, setNewProductOpen] = useState(false)
    const [newCollectionOpen, setNewCollectionOpen] = useState(false)
    const [settingsOpen, setSettingsOpen] = useState(false)

    const createOptions = [
        {
            icon: assetPath('icons/collection-solid.svg'),
            label: 'Collection',
            description: 'Create a new collection',
            value: 'collection',
        },
        // {
        //     icon: assetPath('icons/look-solid.svg'),
        //     label: 'Look',
        //     description: 'Create a new look',
        //     value: 'look',
        // },
        {
            icon: assetPath('icons/product-solid.svg'),
            label: 'Product',
            description: 'Create a new product',
            value: 'product',
        },
    ]

    const handleCreateOptionSelect = (value: string) => {
        withAuth(() => {
            if (value === 'collection') {
                setNewCollectionOpen(true)
            } else if (value === 'look') {
                navigate({
                    to: '/looks/create',
                    search: { collectionId: undefined, from: undefined },
                })
            } else if (value === 'product') {
                setNewProductOpen(true)
            }
        })()
    }

    const handleNewProductCreated = (newId: string) => {
        setNewProductOpen(false)
        navigate({ to: '/products/$id', params: { id: newId } })
    }

    const handleNewCollectionCreated = (newId: string) => {
        setNewCollectionOpen(false)
        navigate({ to: '/collections/$id', params: { id: newId } })
    }

    return (
        <>
            <nav
                ref={navRef}
                className="font-inter-tight text-dark glass-background glass-background--flush fixed z-30 mx-4 top-(--safe-area-top) mt-[14px] grid h-16 w-full max-w-[calc(100%-2rem)] grid-cols-[1fr_minmax(300px,700px)_1fr] items-center justify-between gap-6 rounded-[20px] px-8"
            >
                {/* Left: Logo */}
                <Link to="/" aria-label="home" className="justify-self-start">
                    <img
                        src={assetPath('logo/logo.svg')}
                        width={180}
                        height={42}
                        loading="eager"
                        className="min-w-[180px]"
                    />
                </Link>

                {/* Center: Search Bar */}
                <DesktopSearchBar />

                {/* Right: Actions */}
                <div className="flex items-center gap-3 justify-self-end lg:gap-6 xl:gap-9">
                    <Link
                        to="/"
                        className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl"
                    >
                        <HomeIcon
                            width={36}
                            height={36}
                            color="black"
                            fill="black"
                        />
                    </Link>
                    <button
                        type="button"
                        className="flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-xl bg-black"
                        onClick={() => setCreateOptionsOpen(!createOptionsOpen)}
                        ref={createButtonRef}
                    >
                        <Plus width={36} height={36} color="white" />
                    </button>

                    {user ? (
                        /* Avatar + Chevron + Popup when authenticated */
                        <UserMenu
                            user={user}
                            userId={userId}
                            profileImage={profileImage}
                            navigate={navigate}
                            onSettingsClick={() => setSettingsOpen(true)}
                        />
                    ) : (
                        /* Login button when not authenticated */
                        <Link
                            to="/login"
                            className="text-14 rounded-lg bg-gray-400 px-4 py-2 font-normal text-white transition-colors hover:bg-gray-600 whitespace-nowrap"
                        >
                            Log in
                        </Link>
                    )}
                </div>
            </nav>
            <div className="hide-popup-button">
                <CreateOptionsDropdown
                    isOpen={createOptionsOpen}
                    onClose={() => setCreateOptionsOpen(false)}
                    triggerRef={createButtonRef}
                    options={createOptions}
                    onOptionSelect={handleCreateOptionSelect}
                />

                {newProductOpen && (
                    <CreateProductFlow
                        onClose={() => setNewProductOpen(false)}
                        onProductCreated={handleNewProductCreated}
                    />
                )}

                {newCollectionOpen && (
                    <CreateCollectionFlow
                        open={newCollectionOpen}
                        onClose={() => setNewCollectionOpen(false)}
                        onCollectionCreated={handleNewCollectionCreated}
                    />
                )}

                {settingsOpen && (
                    <PopupSettings
                        open={settingsOpen}
                        setOpen={setSettingsOpen}
                    />
                )}
            </div>
        </>
    )
}
